import React, { CSSProperties } from 'react';
import { Fit } from '../enums/Fit.enum';
import { SelectionType } from '../enums/SelectionType.enum';
import { useProductSelection } from '../hooks/useProductSelection';
import { ViewProduct } from '../models/ViewProduct.model';
import Button from '../root/components/buttons/Button';
import { isRecoilProps } from '../util';
import styles from './ProductFitSwatches.module.scss';

type Props = CallbackProps | RecoilProps;

interface CallbackProps {
  product: ViewProduct;
  style?: CSSProperties;
  selectedFit: Fit | undefined;
  setSelectedFit: (fit: Fit) => void;
}

interface RecoilProps {
  product: ViewProduct;
  selectionType: SelectionType;
  selectionKey: string;
  style?: CSSProperties;
}

export const ProductFitSwatches: React.FC<Props> = (props) => {
  if (isRecoilProps<RecoilProps>(props)) {
    return <RecoilProductFitSwatches {...props} />;
  } else {
    return <CallbackProductFitSwatches {...props} />;
  }
};

const CallbackProductFitSwatches: React.FC<CallbackProps> = ({ product, selectedFit, setSelectedFit, style }) => {
  if (!product.fits?.length) return <></>;

  return (
    <div className={styles.fitSwatchContainer} style={style}>
      {product.fits.map((f, i) => (
        <FitSwatch key={i} active={selectedFit === f} activate={() => setSelectedFit(f)} fit={f} />
      ))}
    </div>
  );
};

const RecoilProductFitSwatches: React.FC<RecoilProps> = ({ product, selectionType, selectionKey, style }) => {
  const { fit: selectedFit, setFit: setSelectedFit } = useProductSelection(selectionType, selectionKey);

  if (!product.fits?.length) return <></>;

  return <CallbackProductFitSwatches product={product} selectedFit={selectedFit} setSelectedFit={setSelectedFit} style={style} />;
};

interface FitSwatchProps {
  fit: Fit;
  active: boolean;
  activate: (fit: Fit) => void;
}

const FitSwatch: React.FC<FitSwatchProps> = ({ fit, active, activate }) => {
  return (
    <Button
      key={fit}
      fullWidth={false}
      role="radio"
      size={'small'}
      disabled={active}
      aria-label={fit}
      aria-checked={active ? 'true' : 'false'}
      onClick={() => !active && activate(fit)}
      text={fit.toUpperCase()}
    />
  );
};

export default ProductFitSwatches;
